import { useCallback, useEffect } from "react";
import { useRouter } from "next/router";
import {
	useRolePermissionContext,
	useRolePermissionsLoading,
	type RolePermissionContext,
} from "@Front/Stores/RolePermissionsStore";
import Notifications from "@Front/Api/LeCoffreApi/Notary/Notifications/Notifications";
import { Toasts } from "@Front/Stores/Toasts";
import { LoggerService } from "@Front/Services/LoggerService/LoggerService";
import { useOfficeItemsFilter } from "../../hooks/useOfficeItemsFilter";
import { getOfficeItems } from "../constants/officeItems";

type NavigationEffectsParams = {
	pathname: string;
	getAnchoringStatus: () => Promise<undefined>;
	getNotifications: () => Promise<void>;
};

async function loadNotificationsAndShowToasts(canReadNotifications: boolean): Promise<void> {
	if (!canReadNotifications) {
		return;
	}
	try {
		// Header mount only: 401 must not wipe the notary session (banner is non-critical); background load, no toast.
		const notifications = await Notifications.getInstance().get(
			{
				where: { read: false },
				include: { notification: true },
				orderBy: { notification: { created_at: "desc" } },
			},
			{ skipSessionFailureOnAuthError: true, background: true },
		);
		if (!Array.isArray(notifications)) {
			LoggerService.error("getNotifications: response is not an array", { notifications });
			return;
		}
		notifications.forEach((notification) => {
			Toasts.getInstance().open({
				title: notification.notification.message,
				uid: notification.uid,
				redirectUrl: notification.notification.redirection_url,
				created_at: notification.notification.created_at,
			});
		});
	} catch (error) {
		LoggerService.error("getNotifications failed", { error });
	}
}

function useNavigationEffects(params: NavigationEffectsParams): void {
	const { pathname, getAnchoringStatus, getNotifications } = params;
	useEffect(() => {
		getAnchoringStatus();
		getNotifications();
	}, [pathname, getNotifications, getAnchoringStatus]);
}

export function useNavigationController(): {
	officeMenuCandidates: ReturnType<typeof useOfficeItemsFilter>;
	permissionContext: RolePermissionContext;
	loadingPermissions: boolean;
} {
	const router = useRouter();
	const permissionContext = useRolePermissionContext();
	const loadingPermissions = useRolePermissionsLoading();
	const pathname = router.pathname;

	const officeMenuCandidates = useOfficeItemsFilter(getOfficeItems());

	const getAnchoringStatus = useCallback(async () => {
		// Ancrage : éviter les getByUid 404 ; une liste /anchors suffirait si besoin futur.
		return undefined;
	}, []);

	const canReadNotifications =
		permissionContext?.isSuperAdmin === true || permissionContext?.rules?.includes("GET notifications") || false;

	const getNotifications = useCallback(
		() => loadNotificationsAndShowToasts(canReadNotifications),
		[canReadNotifications],
	);

	useNavigationEffects({
		pathname,
		getAnchoringStatus,
		getNotifications,
	});

	return {
		officeMenuCandidates,
		permissionContext,
		loadingPermissions,
	};
}
